import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "../styles/Pages.css";

function Smruti() {
  const { year, folder } = useParams();
  const navigate = useNavigate();
  const [years, setYears] = useState([]);
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

  // Helper function to handle image errors
  const handleImageError = (e, identifier) => {
    console.log(`Image failed to load: ${identifier}`);
    setImageLoadErrors((prev) => new Set([...prev, identifier]));
    e.target.src =
      "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjE2MCIgeT0iOTAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2N2VlYSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+";
    e.target.alt = "Image not available";
  };

  // Dynamic discovery functions
  const checkImageExists = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
    });
  };

  const discoverYears = async () => {
    try {
      // Try to load years index file (preferred method)
      const indexResponse = await fetch("/image/smruti/years.json");
      if (indexResponse.ok) {
        const yearsData = await indexResponse.json();
        console.log(`Found ${yearsData.years.length} years in index`);

        // Verify each year's thumbnail exists
        const validYears = [];
        for (const yearItem of yearsData.years) {
          const thumbnailExists = await checkImageExists(yearItem.thumbnail);
          if (thumbnailExists) {
            validYears.push(yearItem);
          }
        }

        return validYears.sort((a, b) => b.year - a.year); // Sort descending
      }
    } catch (error) {
      console.log("No years.json found, falling back to discovery");
    }

    // Fallback: Auto-discovery for the last few years
    const currentYear = new Date().getFullYear();
    const yearRange = Array.from({ length: 5 }, (_, i) => currentYear - i); // Check last 5 years
    const availableYears = [];

    for (const year of yearRange) {
      const thumbnailExists = await checkImageExists(
        `/image/smruti/${year}/thumbnail.jpg`,
      );
      if (thumbnailExists) {
        availableYears.push({
          year: year.toString(),
          thumbnail: `/image/smruti/${year}/thumbnail.jpg`,
        });
      }
    }

    return availableYears.sort((a, b) => b.year - a.year); // Sort descending
  };

  const discoverFoldersInYear = async (selectedYear) => {
    const discoveredFolders = [];

    try {
      // Try to load folder index file (preferred method for getting actual directories)
      const indexResponse = await fetch(
        `/image/smruti/${selectedYear}/folders.json`,
      );
      if (indexResponse.ok) {
        const foldersData = await indexResponse.json();
        console.log(
          `Found ${foldersData.folders.length} folders in ${selectedYear}`,
        );

        // Process each folder from the index
        for (const folderItem of foldersData.folders) {
          let folderName, thumbnailPath;

          // Handle both string format and object format
          if (typeof folderItem === "string") {
            // Old format: just folder name
            folderName = folderItem;
            thumbnailPath = await findFirstThumbnail(selectedYear, folderName);
          } else {
            // New format: object with name and thumbnail
            folderName = folderItem.name;
            thumbnailPath = folderItem.thumbnail;

            // Verify the specified thumbnail exists
            const thumbnailExists = await checkImageExists(thumbnailPath);
            if (!thumbnailExists) {
              // Fall back to auto-discovery if specified thumbnail doesn't exist
              thumbnailPath = await findFirstThumbnail(
                selectedYear,
                folderName,
              );
            }
          }

          if (thumbnailPath) {
            discoveredFolders.push({
              name: folderName,
              thumbnail: thumbnailPath,
            });
          }
        }

        return discoveredFolders.sort((a, b) => a.name.localeCompare(b.name));
      }
    } catch (error) {
      console.log("No folders.json found, using manual discovery");
    }

    // Fallback: Manual discovery for known folders (when no index file exists)
    const knownFolders = ["2025-10-22-Arati-Walking", "2025-11-13-Sabha"];

    for (const folderName of knownFolders) {
      const thumbnailPath = await findFirstThumbnail(selectedYear, folderName);
      if (thumbnailPath) {
        discoveredFolders.push({
          name: folderName,
          thumbnail: thumbnailPath,
        });
      }
    }

    return discoveredFolders.sort((a, b) => a.name.localeCompare(b.name));
  };

  const findFirstThumbnail = async (selectedYear, folderName) => {
    // Common thumbnail file naming patterns
    const thumbnailPatterns = [
      // Numbered patterns
      "thumb1.jpg",
      "thumb1.jpeg",
      "thumb1.JPG",
      "thumb1.JPEG",
      "thumbnail1.jpg",
      "thumbnail1.jpeg",
      "thumbnail1.JPG",
      "thumbnail1.JPEG",
      "1.jpg",
      "1.jpeg",
      "1.JPG",
      "1.JPEG",
      "01.jpg",
      "01.jpeg",
      "01.JPG",
      "01.JPEG",

      // IMG patterns
      "IMG_001.jpg",
      "IMG_001.JPG",
      "IMG_001.jpeg",
      "IMG_001.JPEG",
      "IMG_01.jpg",
      "IMG_01.JPG",
      "IMG_01.jpeg",
      "IMG_01.JPEG",
      "IMG_1.jpg",
      "IMG_1.JPG",
      "IMG_1.jpeg",
      "IMG_1.JPEG",

      // DSC patterns
      "DSC_001.jpg",
      "DSC_001.JPG",
      "DSC_001.jpeg",
      "DSC_001.JPEG",
      "DSC001.jpg",
      "DSC001.JPG",
      "DSC001.jpeg",
      "DSC001.JPEG",

      // Date-based patterns
      `${folderName.substring(0, 10)}-01.jpg`,
      `${folderName.substring(0, 10)}-01.JPG`,
      `${folderName.substring(0, 10)}-1.jpg`,
      `${folderName.substring(0, 10)}-1.JPG`,

      // Generic patterns
      "photo1.jpg",
      "photo1.JPG",
      "pic1.jpg",
      "pic1.JPG",
      "image1.jpg",
      "image1.JPG",
      "img1.jpg",
      "img1.JPG",
    ];

    for (const pattern of thumbnailPatterns) {
      const thumbnailPath = `/image/smruti/${selectedYear}/${folderName}/thumbnail/${pattern}`;
      const exists = await checkImageExists(thumbnailPath);
      if (exists) {
        return thumbnailPath;
      }
    }

    return null;
  };

  const discoverImagesInFolder = async (selectedYear, selectedFolder) => {
    try {
      // Try to load images index file (preferred method)
      const indexResponse = await fetch(
        `/image/smruti/${selectedYear}/${selectedFolder}/images.json`,
      );
      if (indexResponse.ok) {
        const imagesData = await indexResponse.json();
        console.log(
          `Found ${imagesData.images.length} images in ${selectedFolder}`,
        );

        // Verify each image's thumbnail exists
        const validImages = [];
        for (const imageItem of imagesData.images) {
          const thumbnailExists = await checkImageExists(imageItem.thumbnail);
          if (thumbnailExists) {
            validImages.push(imageItem);
          }
        }

        return validImages;
      }
    } catch (error) {
      console.log("No images.json found, falling back to discovery");
    }

    // Fallback: Auto-discovery using common patterns
    const discoveredImages = [];

    // Check for images with various naming patterns
    for (let i = 1; i <= 20; i++) {
      // Check up to 20 images
      const imageNum = i.toString().padStart(2, "0");

      // Multiple naming pattern attempts
      const namingPatterns = [
        {
          thumbnail: `/image/smruti/${selectedYear}/${selectedFolder}/thumbnail/thumb${i}.jpg`,
          full: `/image/smruti/${selectedYear}/${selectedFolder}/image${i}.jpg`,
        },
        {
          thumbnail: `/image/smruti/${selectedYear}/${selectedFolder}/thumbnail/${selectedFolder}-${imageNum}.jpg`,
          full: `/image/smruti/${selectedYear}/${selectedFolder}/${selectedFolder}-${imageNum}.jpg`,
        },
        {
          thumbnail: `/image/smruti/${selectedYear}/${selectedFolder}/thumbnail/IMG_${imageNum}.jpg`,
          full: `/image/smruti/${selectedYear}/${selectedFolder}/IMG_${imageNum}.jpg`,
        },
      ];

      let imageFound = false;
      for (const pattern of namingPatterns) {
        const thumbnailExists = await checkImageExists(pattern.thumbnail);
        if (thumbnailExists) {
          discoveredImages.push(pattern);
          imageFound = true;
          break;
        }
      }

      // If no image found for this number, assume no more images
      if (!imageFound && i > 3) {
        // Allow some gaps for first 3 images
        break;
      }
    }

    return discoveredImages;
  };

  useEffect(() => {
    const loadContent = async () => {
      setLoading(true);

      try {
        if (!year && !folder) {
          // Discover and show years
          const discoveredYears = await discoverYears();
          setYears(discoveredYears);
        } else if (year && !folder) {
          // Discover and show folders for selected year
          const discoveredFolders = await discoverFoldersInYear(year);
          setFolders(discoveredFolders);
        } else if (year && folder) {
          // Discover and show images for selected folder
          const discoveredImages = await discoverImagesInFolder(year, folder);
          setImages(discoveredImages);
        }
      } catch (error) {
        console.error("Error loading content:", error);
        // Set empty arrays on error
        setYears([]);
        setFolders([]);
        setImages([]);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [year, folder]);

  const openImageModal = (index) => {
    setSelectedImageIndex(index);
  };

  const closeImageModal = () => {
    setSelectedImageIndex(null);
  };

  const goToPrevious = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goToNext = () => {
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const handleKeyDown = (e) => {
    if (selectedImageIndex !== null) {
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
      if (e.key === "Escape") closeImageModal();
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImageIndex]);

  const getBreadcrumb = () => {
    const crumbs = [{ label: "Smruti", path: "/download/smruti" }];

    if (year) {
      crumbs.push({ label: year, path: `/download/smruti/${year}` });
    }

    if (folder) {
      crumbs.push({
        label: folder,
        path: `/download/smruti/${year}/${folder}`,
      });
    }

    return crumbs;
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-container pravachan-page">
      <div className="page-header">
        <h1>Smruti Gallery</h1>

        {/* Breadcrumb Navigation */}
        <div className="breadcrumb">
          {getBreadcrumb().map((crumb, index) => (
            <span key={index}>
              <button
                onClick={() => navigate(crumb.path)}
                className="breadcrumb-link"
              >
                {crumb.label}
              </button>
              {index < getBreadcrumb().length - 1 && " > "}
            </span>
          ))}
        </div>
      </div>

      <div className="page-body">
        {/* Display Years */}
        {!year && !folder && (
          <div className="video-grid">
            {years.map((yearData) => (
              <div
                key={yearData.year}
                className="video-card"
                onClick={() => navigate(`/download/smruti/${yearData.year}`)}
              >
                <div className="video-thumbnail">
                  <img
                    src={yearData.thumbnail}
                    alt={`Year ${yearData.year}`}
                    onError={(e) =>
                      handleImageError(e, `year-${yearData.year}`)
                    }
                  />
                </div>
                <div className="video-info">
                  <h3>{yearData.year}</h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Display Folders for Selected Year */}
        {year && !folder && (
          <div className="video-grid">
            {folders.map((folderData) => (
              <div
                key={folderData.name}
                className="video-card"
                onClick={() =>
                  navigate(`/download/smruti/${year}/${folderData.name}`)
                }
              >
                <div className="video-thumbnail">
                  <img
                    src={folderData.thumbnail}
                    alt={folderData.name}
                    onError={(e) =>
                      handleImageError(e, `folder-${folderData.name}`)
                    }
                  />
                </div>
                <div className="video-info">
                  <h3>{folderData.name}</h3>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Display Images for Selected Folder */}
        {year && folder && (
          <div className="video-grid">
            {images.map((image, index) => (
              <div
                key={index}
                className="video-card"
                onClick={() => openImageModal(index)}
              >
                <div className="video-thumbnail">
                  <img
                    src={image.thumbnail}
                    alt={`Image ${index + 1}`}
                    onError={(e) => handleImageError(e, `image-${index}`)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImageIndex !== null && (
        <div className="image-modal" onClick={closeImageModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeImageModal}>
              ×
            </button>

            <button className="modal-nav modal-prev" onClick={goToPrevious}>
              &#8249;
            </button>

            <img
              src={images[selectedImageIndex]?.full}
              alt={`Full size ${selectedImageIndex + 1}`}
              onError={(e) => {
                e.target.src =
                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgZmlsbD0iI2Y4ZjlmYSIvPjx0ZXh0IHg9IjQwMCIgeT0iMzAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IiM2NjdlZWEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgQXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                e.target.alt = "Image not available";
              }}
            />

            <button className="modal-nav modal-next" onClick={goToNext}>
              &#8250;
            </button>

            <div className="modal-info">
              {selectedImageIndex + 1} of {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Smruti;
